class LinkedProducts extends HTMLElement {
  constructor() {
    super();
    this._controllers = [];
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onLinkedProductChange = this._onLinkedProductChange.bind(this);
    this._onFoxThemeReady = this._onFoxThemeReady.bind(this);
  }

  connectedCallback() {
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      this._init();
    } else {
      this._waitForFoxTheme();
    }
  }

  disconnectedCallback() {
    this._teardown();
  }

  // Wait for FoxTheme to load with fallback to direct event listeners
  _waitForFoxTheme() {
    const checkInterval = setInterval(() => {
      if (window.FoxTheme && window.FoxTheme.pubsub) {
        clearInterval(checkInterval);
        this._init();
      }
    }, 100);

    document.addEventListener('DOMContentLoaded', this._onFoxThemeReady);
    
    setTimeout(() => {
      clearInterval(checkInterval);
      if (window.FoxTheme && window.FoxTheme.pubsub) {
        this._init();
      } else {
        this._initWithFallback();
      }
    }, 5000);
  }

  _onFoxThemeReady() {
    document.removeEventListener('DOMContentLoaded', this._onFoxThemeReady);
    if (window.FoxTheme && window.FoxTheme.pubsub && !this.initialized) {
      this._init();
    }
  }

  // Initialize with pubsub events
  _init() {
    if (this.initialized) return;
    this._teardown();

    this.selectedVariantTitle = this._computeCurrentVariantTitle();
    this.linkedProducts = this.querySelectorAll("[data-linked-product]");
    this.mainVariantIdInput = document.querySelector("[data-main-variant-id-input]");
    this.sellingPlanIdInput = document.querySelector("[data-selling-plan-id-input]");

    try {
      // Listen for variant option changes (before DOM update)
      this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        this._onVariantChange
      );

      // Listen for variant changes after DOM updates (critical for re-renders)
      this.onVariantChangeAfterUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this._onVariantChangeAfterUpdate.bind(this)
      );
    } catch (e) {
      this._initWithFallback();
      return;
    }

    this._listen(document, "product-page:rerender", this._onRerender);
    this._listen(this, "change", this._onLinkedProductChange);

    this.updateLinkedProductValues();
    setTimeout(() => this.updateSelectedLinkedProductForm(), 100);
    
    this.initialized = true;
  }

  // Fallback initialization with direct DOM event listeners
  _initWithFallback() {
    if (this.initialized) return;
    this._teardown();

    this.selectedVariantTitle = this._computeCurrentVariantTitle();
    this.linkedProducts = this.querySelectorAll("[data-linked-product]");
    this.mainVariantIdInput = document.querySelector("[data-main-variant-id-input]");
    this.sellingPlanIdInput = document.querySelector("[data-selling-plan-id-input]");

    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      this._listen(variantSelects, 'change', (event) => {
        setTimeout(() => {
          this._onVariantChange({
            data: { event: event, target: event.target, selectedOptionValues: [] }
          });
        }, 50);
      });
    }

    this._listen(document, "product-page:rerender", this._onRerender);
    this._listen(this, "change", this._onLinkedProductChange);

    this.updateLinkedProductValues();
    setTimeout(() => this.updateSelectedLinkedProductForm(), 100);
    
    this.initialized = true;
  }

  _listen(target, type, handler, options) {
    const ctl = new AbortController();
    target.addEventListener(type, handler, { ...(options || {}), signal: ctl.signal });
    this._controllers.push(ctl);
  }

  _teardown() {
    if (!this._controllers) return;
    
    this._controllers.forEach(ctl => {
      try { ctl.abort(); } catch (_) {}
    });
    this._controllers.length = 0;
    
    if (this.onVariantChangeUnsubscriber) {
      try { this.onVariantChangeUnsubscriber(); } catch (e) {}
    }
    if (this.onVariantChangeAfterUpdateUnsubscriber) {
      try { this.onVariantChangeAfterUpdateUnsubscriber(); } catch (e) {}
    }
  }

  // Handle variant changes after theme DOM updates (prevents 422 errors)
  _onVariantChangeAfterUpdate(message) {
    const {
      data: { sectionId, html, variant },
    } = message;

    const productInfo = this.closest("product-info");
    if (productInfo && productInfo.dataset.section !== sectionId) return;

    // Re-cache DOM references after theme updates
    this.mainVariantIdInput = document.querySelector(
      "[data-main-variant-id-input]"
    );
    this.sellingPlanIdInput = document.querySelector(
      "[data-selling-plan-id-input]"
    );
    this.linkedProducts = this.querySelectorAll("[data-linked-product]");

    this.selectedVariantTitle = variant
      ? variant.title
      : this._computeCurrentVariantTitle();

    if (this.selectedVariantTitle) {
      this.updateLinkedProductValues();
    }

    this._restoreLinkedProductSelection();
  }

  _onRerender() {
    this._restoreLinkedProductSelection();
  }

  // Build variant title from selected options (e.g., "Large / Red")
  _computeCurrentVariantTitle() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return '';
    
    const selectedOptions = Array.from(variantSelects.querySelectorAll(
      'select option[selected], input[type="radio"]:checked'
    ));
    
    if (selectedOptions.length === 0) return '';
    
    const optionValues = selectedOptions.map(option => {
      return option.tagName === 'OPTION' ? option.textContent.trim() : option.value;
    }).filter(value => value);
    
    return optionValues.join(' / ');
  }

  _getCurrentSelectedVariant() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;
    
    const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
    if (!selectedVariantScript) return null;
    
    try {
      return JSON.parse(selectedVariantScript.textContent);
    } catch (e) {
      return null;
    }
  }

  _onVariantChange(message) {
    const { event, target, selectedOptionValues } = message.data || message;
    
    this.selectedVariantTitle = this._computeCurrentVariantTitle();
    
    if (this.selectedVariantTitle) {
      this.updateLinkedProductValues();
    }
    
    this.updateSelectedLinkedProductForm();
  }

  _onLinkedProductChange(event) {
    const input = event.target.closest("[data-linked-product-input]");
    if (!input || !this.contains(input)) return;

    this.updateSelectedLinkedProductForm();

    // Update URL params
    const linkedProduct = input.closest("[data-linked-product]");
    const subscriptionOption = linkedProduct?.getAttribute("data-subscription-option");
    const sellingPlanId = linkedProduct?.getAttribute("data-selling-plan-id");
    
    const url = new URL(window.location);
    if (subscriptionOption) url.searchParams.set("subscription", subscriptionOption);
    if (sellingPlanId) url.searchParams.set("selling_plan", sellingPlanId);
    window.history.replaceState({}, "", url.toString());
  }

  // Update main form inputs with selected linked product's matching variant
  updateSelectedLinkedProductForm() {
    let selected = this.querySelector("[data-linked-product-input]:checked");
    
    if (!selected) {
      const firstInput = this.querySelector("[data-linked-product-input]");
      if (firstInput) {
        firstInput.checked = true;
        selected = firstInput;
      } else {
        return;
      }
    }

    const linkedProduct = selected.closest("[data-linked-product]");
    if (!linkedProduct) return;

    const currentVariantTitle = this.selectedVariantTitle || this._computeCurrentVariantTitle();
    if (!currentVariantTitle) return;

    const productVariants = linkedProduct.querySelector("[data-linked-product-variants]");
    if (!productVariants) return;

    let variantsJSON = [];
    try {
      variantsJSON = JSON.parse(productVariants.textContent || "[]");
    } catch (e) {
      return;
    }

    // Find matching variant in selected linked product
    const matchedVariant = variantsJSON.find(variant => variant.title === currentVariantTitle);

    if (matchedVariant) {
      selected.value = matchedVariant.id;

      if (this.mainVariantIdInput) {
        this.mainVariantIdInput.value = matchedVariant.id;
      }

      const sellingPlanId = matchedVariant.selling_plan_allocations?.[0]?.selling_plan_id;
      
      if (sellingPlanId) {
        linkedProduct.setAttribute("data-selling-plan-id", sellingPlanId);
        if (this.sellingPlanIdInput) {
          this.sellingPlanIdInput.value = sellingPlanId;
        }
      } else {
        linkedProduct.removeAttribute("data-selling-plan-id");
        if (this.sellingPlanIdInput) {
          this.sellingPlanIdInput.value = '';
        }
      }

      // Update price display
      this._updateLinkedProductPrice(linkedProduct, matchedVariant);

      this.dispatchEvent(new CustomEvent('linked-product-variant-updated', {
        bubbles: true,
        detail: {
          variantId: matchedVariant.id,
          variantTitle: matchedVariant.title,
          sellingPlanId: sellingPlanId
        }
      }));
    } else {
      // Fallback to first available variant
      const fallbackVariant = variantsJSON.find(v => v.available) || variantsJSON[0];
      if (fallbackVariant) {
        selected.value = fallbackVariant.id;
        if (this.mainVariantIdInput) {
          this.mainVariantIdInput.value = fallbackVariant.id;
        }

        // Update price for fallback variant too
        this._updateLinkedProductPrice(linkedProduct, fallbackVariant);
      }
    }
  }

  // Update all linked products with matching variants when main product variant changes
  updateLinkedProductValues() {
    this.linkedProducts = this.querySelectorAll("[data-linked-product]");

    this.linkedProducts.forEach((product) => {
      const productVariants = product.querySelector("[data-linked-product-variants]");
      if (!productVariants) return;

      let variantsJSON = [];
      try {
        variantsJSON = JSON.parse(productVariants.textContent || "[]");
      } catch (e) {
        return;
      }

      const matchedVariant = variantsJSON.find(variant => variant.title === this.selectedVariantTitle);

      if (matchedVariant) {
        const linkedProductInput = product.querySelector("[data-linked-product-input]");
        if (linkedProductInput) {
          linkedProductInput.value = matchedVariant.id;
        }

        const sellingPlanId = matchedVariant.selling_plan_allocations?.[0]?.selling_plan_id;
        if (sellingPlanId) {
          product.setAttribute("data-selling-plan-id", sellingPlanId);
        } else {
          product.removeAttribute("data-selling-plan-id");
        }

        // Update price display
        this._updateLinkedProductPrice(product, matchedVariant);

        // Update selling plan options for the matched variant
        this._updateLinkedProductSellingPlans(product, matchedVariant);
      }
    });

    // Handle URL pre-selection
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionParam = urlParams.get("subscription");
    if (subscriptionParam) {
      this.querySelectorAll("[data-linked-product-input]").forEach((input) => {
        const linkedProduct = input.closest("[data-linked-product]");
        const option = linkedProduct?.getAttribute("data-subscription-option");
        if (option === subscriptionParam && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }
  }

  // Restore linked product selection after DOM re-renders
  _restoreLinkedProductSelection() {
    let selected = this.querySelector("[data-linked-product-input]:checked");
    
    if (!selected) {
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionParam = urlParams.get("subscription");
      
      if (subscriptionParam) {
        this.querySelectorAll("[data-linked-product-input]").forEach((input) => {
          const linkedProduct = input.closest("[data-linked-product]");
          const option = linkedProduct?.getAttribute("data-subscription-option");
          if (option === subscriptionParam) {
            input.checked = true;
            selected = input;
          }
        });
      }
      
      if (!selected) {
        const firstInput = this.querySelector("[data-linked-product-input]");
        if (firstInput) {
          firstInput.checked = true;
          selected = firstInput;
        }
      }
    }

    if (selected) {
      this.updateSelectedLinkedProductForm();
    }
  }

  // Helper method to update price display for a linked product
  _updateLinkedProductPrice(linkedProduct, matchedVariant) {
    const priceElement = linkedProduct.querySelector("[data-linked-product-price]");
    if (!priceElement) return;

    // Get the selling plan allocation price if available
    const sellingPlanAllocation = matchedVariant.selling_plan_allocations?.[0];
    let priceToDisplay = matchedVariant.price;

    if (sellingPlanAllocation && sellingPlanAllocation.price !== undefined) {
      priceToDisplay = sellingPlanAllocation.price;
    }

    // Format the price using the existing money format from FoxTheme
    if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney) {
      // Get the money format from the theme or use a sensible default
      const moneyFormat = window.theme?.moneyFormat || (window.FoxTheme.theme && window.FoxTheme.theme.moneyFormat) || '${{amount}}';
      priceElement.innerHTML = window.FoxTheme.Currency.formatMoney(priceToDisplay, moneyFormat);
    } else {
      // Fallback to basic formatting - prices are typically in cents
      const formattedPrice = (priceToDisplay / 100).toFixed(2);
      priceElement.innerHTML = `$${formattedPrice}`;
    }
  }

  // Helper method to update selling plan options for a linked product variant
  _updateLinkedProductSellingPlans(linkedProduct, matchedVariant) {
    // For linked products, we typically only show one selling plan option per product
    // The selling plan is already set via data-selling-plan-id attribute
    // But we should update the displayed price to reflect the selling plan price

    const sellingPlanAllocation = matchedVariant.selling_plan_allocations?.[0];
    if (!sellingPlanAllocation) return;

    // Update the selling plan ID attribute
    linkedProduct.setAttribute("data-selling-plan-id", sellingPlanAllocation.selling_plan_id);

    // Update the input value to reflect the new variant with selling plan
    const linkedProductInput = linkedProduct.querySelector("[data-linked-product-input]");
    if (linkedProductInput) {
      linkedProductInput.value = matchedVariant.id;
    }

    // The price is already updated by _updateLinkedProductPrice, so we're done here
    // In the future, if we need to show multiple selling plan options per linked product,
    // we would need to regenerate the selling plan radio buttons similar to gift-selling-options.js
  }
}

if (!customElements.get("linked-products")) {
  customElements.define("linked-products", LinkedProducts);
}